import React, { useState, useEffect, useRef, useCallback } from "react";
import './Gallery.css'
import ImageViewer from "../ImageViewer/ImageViewer";
import { ImageList, ImageListItem, Skeleton, Box, CircularProgress } from "@mui/material";

const sizes = [].concat(...Array.from({ length: 84 }, () => [1, 1, 2, 2, 1, 1]));

const GalleryItem = React.forwardRef(({ image, index, onClick, cols, rows }, ref) => {
    const [loaded, setLoaded] = useState(false);

    return (
        <ImageListItem 
            ref={ref}
            cols={cols} 
            rows={rows} 
            onClick={onClick}
            sx={{ 
                cursor: 'pointer', 
                position: 'relative',
                height: '100% !important', 
                minHeight: rows * 150,
                overflow: 'hidden',
                bgcolor: 'grey.300'
            }}
        >
            {!loaded && (
                <Skeleton 
                    variant="rectangular" 
                    animation="wave"
                    sx={{ 
                        position: 'absolute', top: 0, left: 0, 
                        width: '100%', height: '100%', zIndex: 1,
                        bgcolor: 'grey.300' 
                    }}
                />
            )}
            <img
                src={`${image}?fit=crop&auto=format`}
                alt={`Item ${index}`}
                onLoad={() => setLoaded(true)}
                style={{ 
                    width: '100%', height: '100%', objectFit: 'cover',
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.4s ease-in-out' 
                }}
            />
        </ImageListItem>
    );
});

function Gallery(props) {
    const [viewerHidden, setViewerHidden] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [displayCount, setDisplayCount] = useState(12);
    const [hasMore, setHasMore] = useState(props.images.length > 12);
    
    const observer = useRef();

    const lastImageElementRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();
        
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setDisplayCount(prev => {
                    const nextCount = prev + 12;
                    if (nextCount >= props.images.length) setHasMore(false);
                    return nextCount;
                });
            }
        });

        if (node) observer.current.observe(node);
    }, [hasMore, props.images.length]);

    useEffect(() => {
        setDisplayCount(12);
        setHasMore(props.images.length > 12);
        setSelectedImage(null);
    }, [props.images]);

    const selectImage = (index) => {
        setSelectedImage(index);
        setViewerHidden(false);
        document.body.style.overflow = 'hidden';
    }

    const hideViewer = () => {
        setViewerHidden(true);
        document.body.style.overflow = 'auto';
    }

    return (
        <>
            <ImageList variant="quilted" cols={4}>
                {props.images.slice(0, displayCount).map((image, index) => {
                    const isLastItem = index === displayCount - 1;
                    return (
                        <GalleryItem 
                            ref={isLastItem ? lastImageElementRef : null}
                            key={`${image}-${index}`}
                            image={image}
                            index={index}
                            cols={sizes[index] || 1}
                            rows={sizes[index] || 1}
                            onClick={() => selectImage(index)}
                        />
                    );
                })}
            </ImageList>

            {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress color="inherit" sx={{ color: 'grey.700' }} />
                </Box>
            )}

            <ImageViewer
                hideViewer={hideViewer}
                selectedImage={selectedImage}
                images={props.images.slice(0, displayCount)}
                hidden={viewerHidden}
                switchImage={setSelectedImage} 
            />
        </>
    );
}
export default Gallery;